import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Checkbox,
  Dialog,
  Flex,
  FlexColumn,
} from '@elementary/common-ui/components';
import { pluralizeString } from '@elementary/common-ui/utils/common';
import {
  INCIDENT_GRAPHQL_DETAIL,
  INCIDENT_LIST_FILTER_OPTIONS,
  INCIDENT_LIST_GROUP_SUMMARY,
  INCIDENT_LIST_PAGED,
} from '@elementary/common-ui/constants/queryKeys';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import type { ChipStatus } from '@elementary/common-ui/components/Chip/StatusChip';
import useSnackbar from 'shared/hooks/useSnackbar';
import {
  fontSize,
  fontWeight,
  radius,
  space,
} from '@elementary/common-ui/style/utils';
import { isIncidentReadOnly } from '../../utils/incidentState';
import { getMonitorChipStatus } from '../../statusUtils';
import type { IncidentDetail, Monitor } from '../../types';
import { splitIncident } from '../../api';
import IncidentMonitorCard from './IncidentMonitorCard';
import IncidentRestrictedMonitorCard from './IncidentRestrictedMonitorCard';

type FilterType = 'all' | 'active' | 'access' | 'asset' | 'test';

const MONITOR_SEVERITY_ORDER: ChipStatus[] = [
  'error',
  'fail',
  'runtime_error',
  'warn',
  'warning',
  'pass',
  'passed',
  'success',
  'active',
  'skipped',
  'skip',
  'noData',
  'idle',
  'resolved',
];

const isRestrictedMonitor = (monitor: Monitor): boolean =>
  monitor.asset?.__typename === 'RestrictedAsset';

type AccessibleMonitor = Monitor & { signal: { id: string } };
const isAccessibleMonitor = (m: Monitor): m is AccessibleMonitor =>
  !isRestrictedMonitor(m) && m.signal.id != null;

const getMonitorSeverityRank = (monitor: Monitor): number => {
  const status = getMonitorChipStatus(monitor.signal);
  const index = MONITOR_SEVERITY_ORDER.indexOf(status);
  return index === -1 ? MONITOR_SEVERITY_ORDER.length : index;
};

const sortMonitorsBySeverity = (monitors: Monitor[]): Monitor[] =>
  monitors
    .map((monitor, index) => ({ monitor, index }))
    .sort((a, b) => {
      const severityDiff =
        getMonitorSeverityRank(a.monitor) - getMonitorSeverityRank(b.monitor);
      return severityDiff || a.index - b.index;
    })
    .map(({ monitor }) => monitor);

const TabContent = styled(FlexColumn)`
  gap: ${space('lg')};
  padding: ${space('lg', 0)};
  flex: 1;
  min-height: 0;
  overflow: auto;
`;

const FilterChip = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${space('xs', 'md')};
  height: 24px;
  border-radius: ${radius('xxl')};
  border: ${({ $active, theme }) =>
    $active ? 'none' : `1px solid ${theme.palette.border.emphasis}`};
  background: ${({ $active, theme }) =>
    $active ? theme.palette.surface.inverse : theme.palette.surface.subtle};
  color: ${({ $active, theme }) =>
    $active ? theme.palette.content.inverse : theme.palette.content.primary};
  cursor: pointer;
  font-size: ${fontSize('md')};
  font-weight: ${fontWeight('regular')};
  white-space: nowrap;

  &:hover {
    opacity: 0.8;
  }
`;

const CardsContainer = styled(FlexColumn)`
  gap: ${space('md')};
`;

const FiltersRow = styled(Flex)`
  min-height: 32px;
`;

const CardRow = styled(Flex)`
  align-items: center;
  gap: ${space('lg')};
`;

const ChipDivider = styled.span`
  display: inline-block;
  width: 1px;
  height: 24px;
  background: ${({ theme }) => theme.palette.surface.raised};
  margin: ${space(0, 'xs')};
`;

type Props = {
  incident: IncidentDetail;
};

const IncidentMonitorsTab = ({ incident }: Props) => {
  const [filter, setFilter] = useState<FilterType>('all');
  const [selectedSignalIds, setSelectedSignalIds] = useState<Set<string>>(
    new Set(),
  );
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const envId = useSelectedEnv();
  const queryClient = useQueryClient();
  const { openSnackbar } = useSnackbar();
  const isReadOnly = isIncidentReadOnly(incident);

  const { monitors } = incident;

  const { activeMonitors, accessibleMonitors, assetMonitors, testMonitors } =
    useMemo(() => {
      const accessible: Monitor[] = [];
      const active: Monitor[] = [];
      const asset: Monitor[] = [];
      const test: Monitor[] = [];
      for (const m of monitors) {
        if (m.signal.isActive) {
          active.push(m);
        }
        if (isRestrictedMonitor(m)) {
          continue;
        }
        accessible.push(m);
        if (m.test) {
          test.push(m);
        } else {
          asset.push(m);
        }
      }
      return {
        activeMonitors: active,
        accessibleMonitors: accessible,
        assetMonitors: asset,
        testMonitors: test,
      };
    }, [monitors]);

  const filteredMonitors = useMemo(() => {
    switch (filter) {
      case 'active':
        return activeMonitors;
      case 'access':
        return accessibleMonitors;
      case 'asset':
        return assetMonitors;
      case 'test':
        return testMonitors;
      default:
        return monitors;
    }
  }, [
    filter,
    monitors,
    activeMonitors,
    accessibleMonitors,
    assetMonitors,
    testMonitors,
  ]);
  const sortedMonitors = useMemo(
    () => sortMonitorsBySeverity(filteredMonitors),
    [filteredMonitors],
  );

  const handleToggle = (signalId: string) => {
    setSelectedSignalIds((prev) => {
      const next = new Set(prev);
      if (next.has(signalId)) {
        next.delete(signalId);
      } else {
        next.add(signalId);
      }
      return next;
    });
  };

  const selectedCount = selectedSignalIds.size;
  const hasRestrictedMonitors = monitors.some(isRestrictedMonitor);
  const isFullDetachSelection =
    selectedCount > 0 &&
    selectedCount === accessibleMonitors.length &&
    !hasRestrictedMonitors;
  const showCheckboxes = accessibleMonitors.length > 1;
  const showActiveFilter = activeMonitors.length !== monitors.length;
  const showAccessFilter = accessibleMonitors.length !== monitors.length;
  const showPrimaryFilterDivider = showActiveFilter || showAccessFilter;

  const { mutate: runSplit, isLoading: isSplitting } = useMutation({
    mutationFn: () =>
      splitIncident(envId ?? '', incident.id, [...selectedSignalIds]),
    onSuccess: (response) => {
      queryClient.invalidateQueries([INCIDENT_LIST_PAGED]);
      queryClient.invalidateQueries([INCIDENT_LIST_GROUP_SUMMARY]);
      queryClient.invalidateQueries([INCIDENT_LIST_FILTER_OPTIONS]);
      queryClient.invalidateQueries([INCIDENT_GRAPHQL_DETAIL]);
      setSelectedSignalIds(new Set());
      setConfirmDialogOpen(false);
      const n = response.new_incidents.length;
      const fullyUnmerged = isFullDetachSelection && n === selectedCount;
      openSnackbar({
        variant: 'success',
        message: fullyUnmerged
          ? `Detached into ${pluralizeString(
              n,
              'new incident',
              'new incidents',
            )} and resolved this incident`
          : `Detached into ${pluralizeString(
              n,
              'new incident',
              'new incidents',
            )}`,
      });
    },
    onError: () => {
      setConfirmDialogOpen(false);
      openSnackbar({ variant: 'error', message: 'Detach failed' });
    },
  });

  return (
    <TabContent>
      <FiltersRow gap={1} flexWrap="wrap" alignItems="center">
        <FilterChip $active={filter === 'all'} onClick={() => setFilter('all')}>
          All ({monitors.length})
        </FilterChip>
        {showActiveFilter && (
          <FilterChip
            $active={filter === 'active'}
            onClick={() => setFilter('active')}
          >
            Active ({activeMonitors.length})
          </FilterChip>
        )}
        {showAccessFilter && (
          <FilterChip
            $active={filter === 'access'}
            onClick={() => setFilter('access')}
          >
            With access ({accessibleMonitors.length})
          </FilterChip>
        )}
        {showPrimaryFilterDivider && <ChipDivider aria-hidden />}
        <FilterChip
          $active={filter === 'asset'}
          onClick={() => setFilter('asset')}
        >
          Asset runs ({assetMonitors.length})
        </FilterChip>
        <FilterChip
          $active={filter === 'test'}
          onClick={() => setFilter('test')}
        >
          Tests ({testMonitors.length})
        </FilterChip>

        <Button
          variant="outlined"
          size="extraSmall"
          disabled={selectedCount === 0 || isReadOnly}
          onClick={() => setConfirmDialogOpen(true)}
          sx={{
            marginLeft: 'auto',
            visibility: showCheckboxes ? 'visible' : 'hidden',
          }}
        >
          Detach
        </Button>
      </FiltersRow>

      {sortedMonitors.length === 0 ? (
        <Typography sx={{ color: 'content.tertiary' }}>
          No monitors match the selected filter.
        </Typography>
      ) : (
        <CardsContainer>
          {sortedMonitors.filter(isAccessibleMonitor).map((monitor) => (
            <CardRow key={monitor.signal.id}>
              {showCheckboxes && (
                <Checkbox
                  checked={selectedSignalIds.has(monitor.signal.id)}
                  $noPadding
                  disabled={!monitor.signal.isActive}
                  onChange={() => handleToggle(monitor.signal.id)}
                  aria-label={`Select monitor ${
                    monitor.test?.name ?? monitor.asset?.name ?? ''
                  }`}
                />
              )}
              <Flex flex={1} minWidth={0}>
                <IncidentMonitorCard monitor={monitor} />
              </Flex>
            </CardRow>
          ))}
          {sortedMonitors.filter(isRestrictedMonitor).map((monitor, index) => (
            <IncidentRestrictedMonitorCard
              key={monitor.asset?.id ?? `restricted-${index}`}
            />
          ))}
        </CardsContainer>
      )}

      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirmButtonClick={() => runSplit()}
        title={`Detach ${pluralizeString(
          selectedCount,
          'monitor',
          'monitors',
        )} from this incident`}
        message={
          isFullDetachSelection
            ? 'A new incident will be created for each detached monitor. This incident will be resolved.'
            : 'A new incident will be created for each detached monitor.'
        }
        confirmText="Detach"
        isLoading={isSplitting}
        variant="error"
      />
    </TabContent>
  );
};

export default IncidentMonitorsTab;
